import React, {useEffect} from "react";
import type {ReactNode} from "react";

interface ModalProps {
    open: boolean;
    title: string;
    /** Закрытие по крестику / клику на фон / Esc. Не вызывается во время обязательного шага. */
    onClose: () => void;
    children: ReactNode;
    /** Не давать закрыть (Esc/фон/крестик) — напр. пока не скопировали токен. */
    disableClose?: boolean;
    /** Класс для узких/широких модалок (напр. "modal-sm"). */
    sizeClassName?: string;
}

/**
 * Простой модальный диалог (центрированный, с фоном). До фазы B в проекте
 * модалок не было — сделан минимально и переиспользуемо на CSS-классах
 * .modal-overlay / .modal / .modal-header / .modal-close. Закрытие по Esc
 * и клику на фон, если не включён disableClose.
 */
export function Modal({open, title, onClose, children, disableClose = false, sizeClassName = ""}: ModalProps): JSX.Element | null {
    useEffect(() => {
        if (!open || disableClose) return;
        const onKey = (e: KeyboardEvent): void => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, disableClose, onClose]);

    if (!open) return null;

    const handleOverlay = (): void => {
        if (!disableClose) onClose();
    };

    return (
        <div
            className="modal-overlay"
            onClick={handleOverlay}
            role="dialog"
            aria-modal="true"
            aria-label={title}
        >
            <div className={`modal ${sizeClassName}`.trim()} onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <strong>{title}</strong>
                    {!disableClose && (
                        <button className="modal-close" type="button" onClick={onClose} aria-label="Close">
                            ×
                        </button>
                    )}
                </div>
                <div className="modal-body">{children}</div>
            </div>
        </div>
    );
}
